import { API_BASE } from "../utils/api";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";

const INV_TYPES = {
  shares:        { label: "Shares",            icon: "📈", color: "text-luxury-cta",                        bg: "bg-emerald-50  dark:bg-emerald-900/20", border: "border-emerald-200  dark:border-emerald-500/30" },
  etf:           { label: "ETF",               icon: "📊", color: "text-blue-600 dark:text-blue-400",       bg: "bg-blue-50    dark:bg-blue-900/20",    border: "border-blue-200    dark:border-blue-500/30" },
  unit_trust:    { label: "Unit Trust",         icon: "🏦", color: "text-purple-600 dark:text-purple-400",  bg: "bg-purple-50  dark:bg-purple-900/20",  border: "border-purple-200  dark:border-purple-500/30" },
  retirement_ra: { label: "Retirement Annuity", icon: "🏛️", color: "text-luxury-gold",                      bg: "bg-yellow-50  dark:bg-luxury-elevated", border: "border-yellow-200  dark:border-luxury-gold/30" },
  tfsa:          { label: "Tax-Free Savings",   icon: "🆓", color: "text-teal-600 dark:text-teal-400",      bg: "bg-teal-50    dark:bg-teal-900/20",    border: "border-teal-200    dark:border-teal-500/30" },
  property:      { label: "Property",           icon: "🏠", color: "text-amber-600 dark:text-amber-400",    bg: "bg-amber-50   dark:bg-amber-900/20",   border: "border-amber-200   dark:border-amber-500/30" },
  crypto:        { label: "Crypto",             icon: "₿",  color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-50  dark:bg-orange-900/20",  border: "border-orange-200  dark:border-orange-500/30" },
  cash_savings:  { label: "Cash Savings",       icon: "💵", color: "text-luxury-secondary",                 bg: "bg-luxury-elevated",                   border: "border-luxury-border" },
  other:         { label: "Other",              icon: "📋", color: "text-luxury-secondary",                 bg: "bg-luxury-elevated",                   border: "border-luxury-border" },
};

const SA_INSTITUTIONS = [
  "Allan Gray","Coronation","Ninety One","Sanlam","Old Mutual","Discovery Invest",
  "Momentum","PSG Wealth","FNB","Nedbank","Standard Bank","Absa","Capitec","EasyEquities",
  "10X Investments","Other",
];

function fmt(n) {
  return "R " + Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(n) {
  const sign = n >= 0 ? "+" : "";
  return `${sign}${Number(n).toFixed(2)}%`;
}

function AddInvestmentModal({ onClose, onAdd, token }) {
  const [form, setForm] = useState({
    name: "", investment_type: "shares", amount_invested: "",
    current_value: "", institution: "", interest_rate: "",
    units: "", unit_price: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.amount_invested || !form.current_value) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        amount_invested: parseFloat(form.amount_invested) || 0,
        current_value:   parseFloat(form.current_value)   || 0,
        interest_rate:   parseFloat(form.interest_rate)   || 0,
        units:           parseFloat(form.units)           || 0,
        unit_price:      parseFloat(form.unit_price)      || 0,
      };
      const res = await fetch(`${API_BASE}/api/investments/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) { onAdd(await res.json()); onClose(); }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const inp = "w-full px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 placeholder-luxury-muted";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-luxury-surface border border-luxury-border shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-luxury-border bg-luxury-elevated">
          <div>
            <h3 className="font-bold text-luxury-text text-sm">Add Investment</h3>
            <p className="text-xs text-luxury-muted mt-0.5">Saved permanently to your account</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-luxury-muted hover:text-luxury-text transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-3 max-h-[72vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Name *</label>
              <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Naspers Shares" className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Type *</label>
              <select value={form.investment_type} onChange={e => set("investment_type", e.target.value)} className={inp}>
                {Object.entries(INV_TYPES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Amount Invested (R) *</label>
              <input type="number" value={form.amount_invested} onChange={e => set("amount_invested", e.target.value)} placeholder="e.g. 50000" className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Current Value (R) *</label>
              <input type="number" value={form.current_value} onChange={e => set("current_value", e.target.value)} placeholder="e.g. 62000" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Institution</label>
              <select value={form.institution} onChange={e => set("institution", e.target.value)} className={inp}>
                <option value="">Select institution</option>
                {SA_INSTITUTIONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Interest / Return Rate (%)</label>
              <input type="number" value={form.interest_rate} onChange={e => set("interest_rate", e.target.value)} placeholder="e.g. 8.5" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Units Held</label>
              <input type="number" value={form.units} onChange={e => set("units", e.target.value)} placeholder="e.g. 125" className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Unit Price (R)</label>
              <input type="number" value={form.unit_price} onChange={e => set("unit_price", e.target.value)} placeholder="e.g. 496" className={inp} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Notes</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} placeholder="Any notes..." className={inp + " resize-none"} />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-luxury-border bg-luxury-elevated flex gap-3">
          <button onClick={submit} disabled={saving || !form.name || !form.amount_invested || !form.current_value}
            className="flex-1 py-2.5 rounded-xl bg-luxury-cta hover:bg-luxury-cta-hover disabled:opacity-40 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2">
            {saving && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>}
            {saving ? "Saving..." : "Add Investment"}
          </button>
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-luxury-bg border border-luxury-border text-luxury-secondary text-sm font-semibold hover:bg-luxury-elevated transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Investments() {
  const { token }      = useAuth();
  const { addToast }   = useToast();
  const [investments, setInvestments] = useState([]);
  const [summary, setSummary]         = useState({});
  const [loading, setLoading]         = useState(true);
  const [showAdd, setShowAdd]         = useState(false);
  const [expanded, setExpanded]       = useState(null);
  const [filter, setFilter]           = useState("all");

  const fetchInvestments = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/investments/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInvestments(data.investments || []);
        setSummary(data.summary || {});
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchInvestments(); }, [fetchInvestments]);

  const handleAdd = (inv) => {
    setInvestments(p => [inv, ...p]);
    addToast("Investment added successfully", "success");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this investment?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/investments/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { setInvestments(p => p.filter(i => i.id !== id)); addToast("Investment deleted", "success"); }
    } catch (err) { addToast("Failed to delete", "error"); }
  };

  const filtered = filter === "all" ? investments : investments.filter(i => i.investment_type === filter);

  // Portfolio allocation by type
  const allocation = investments.reduce((acc, inv) => {
    acc[inv.investment_type] = (acc[inv.investment_type] || 0) + inv.current_value;
    return acc;
  }, {});
  const totalVal = summary.total_value || 1;

  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-luxury-elevated border border-luxury-gold/30 flex items-center justify-center">
                <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-luxury-muted">Personal Finance</span>
            </div>
            <h1 className="text-2xl font-bold text-luxury-text">Investment Portfolio</h1>
            <p className="text-sm text-luxury-secondary mt-1">Track all your investments, returns, and portfolio performance.</p>
          </div>
          <button onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 bg-luxury-cta hover:bg-luxury-cta-hover text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm active:scale-95">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Add Investment
          </button>
        </div>
      </div>

      {/* ── Summary Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Invested",   value: fmt(summary.total_invested),                   color: "text-luxury-text",                         accent: "border-t-luxury-border" },
          { label: "Portfolio Value",  value: fmt(summary.total_value),                      color: "text-luxury-gold",                         accent: "border-t-luxury-gold" },
          { label: "Total Gain / Loss",value: fmt(summary.total_gain_loss),                  color: (summary.total_gain_loss||0) >= 0 ? "text-luxury-cta" : "text-red-600 dark:text-red-400", accent: (summary.total_gain_loss||0) >= 0 ? "border-t-luxury-cta" : "border-t-red-400" },
          { label: "Return",           value: fmtPct(summary.gain_loss_pct || 0),            color: (summary.gain_loss_pct||0) >= 0 ? "text-luxury-cta" : "text-red-600 dark:text-red-400", accent: (summary.gain_loss_pct||0) >= 0 ? "border-t-luxury-cta" : "border-t-red-400" },
        ].map(s => (
          <div key={s.label} className={`rounded-2xl p-5 border border-luxury-border border-t-2 bg-luxury-surface hover:scale-[1.02] transition-all ${s.accent}`}>
            <div className={`text-xl lg:text-2xl font-bold mb-1 ${s.color}`}>{s.value}</div>
            <div className="text-xs font-medium text-luxury-secondary uppercase tracking-wide">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Portfolio Allocation ─────────────────────────────────── */}
      {investments.length > 0 && (
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5 mb-6">
          <h3 className="font-semibold text-luxury-text text-sm mb-4">Portfolio Allocation</h3>
          <div className="space-y-2.5">
            {Object.entries(allocation).sort((a,b)=>b[1]-a[1]).map(([type, value]) => {
              const cfg = INV_TYPES[type] || INV_TYPES.other;
              const pct = Math.round((value / totalVal) * 100);
              return (
                <div key={type}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-luxury-secondary">{cfg.icon} {cfg.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-luxury-text">{fmt(value)}</span>
                      <span className="text-xs text-luxury-muted">{pct}%</span>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full bg-luxury-elevated overflow-hidden">
                    <div className={`h-full rounded-full ${cfg.color.includes("cta")?"bg-luxury-cta":cfg.color.includes("gold")?"bg-luxury-gold":cfg.color.includes("blue")?"bg-blue-500":cfg.color.includes("purple")?"bg-purple-500":cfg.color.includes("amber")?"bg-amber-500":cfg.color.includes("teal")?"bg-teal-500":cfg.color.includes("orange")?"bg-orange-500":"bg-luxury-muted"}`}
                      style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filter Tabs ──────────────────────────────────────────── */}
      <div className="flex gap-2 flex-wrap mb-5">
        {[
          { key: "all", label: "All" },
          ...Object.entries(INV_TYPES).map(([k,v]) => ({ key: k, label: `${v.icon} ${v.label}` })),
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
              ${filter === f.key ? "bg-luxury-cta text-white border-transparent" : "bg-luxury-surface border-luxury-border text-luxury-secondary hover:bg-luxury-elevated"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Investment List ──────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i=><div key={i} className="h-24 rounded-2xl animate-pulse bg-luxury-surface border border-luxury-border"/>)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-14 text-center">
          <div className="text-4xl mb-3">📈</div>
          <p className="font-semibold text-luxury-text mb-1">No investments tracked yet</p>
          <p className="text-sm text-luxury-secondary mb-5">Add your shares, ETFs, retirement annuities, and savings to track your portfolio.</p>
          <button onClick={() => setShowAdd(true)} className="px-5 py-2.5 bg-luxury-cta hover:bg-luxury-cta-hover text-white text-sm font-semibold rounded-xl transition-colors">
            Add First Investment
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(inv => {
            const cfg    = INV_TYPES[inv.investment_type] || INV_TYPES.other;
            const isGain = inv.gain_loss >= 0;
            const isOpen = expanded === inv.id;
            return (
              <div key={inv.id} className="rounded-2xl bg-luxury-surface border border-luxury-border overflow-hidden hover:border-luxury-gold/20 transition-all">
                <button onClick={() => setExpanded(isOpen ? null : inv.id)} className="w-full px-5 py-4 flex items-center gap-4 text-left">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border text-lg ${cfg.bg} ${cfg.border}`}>
                    {cfg.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-sm text-luxury-text">{inv.name}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}>{cfg.label}</span>
                    </div>
                    {inv.institution && <p className="text-xs text-luxury-muted">{inv.institution}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-bold text-luxury-gold">{fmt(inv.current_value)}</div>
                    <div className={`text-xs font-semibold ${isGain ? "text-luxury-cta" : "text-red-600 dark:text-red-400"}`}>
                      {isGain ? "▲" : "▼"} {fmtPct(inv.gain_loss_pct)}
                    </div>
                  </div>
                  <svg className={`w-4 h-4 text-luxury-muted transition-transform flex-shrink-0 ${isOpen?"rotate-180":""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>

                {isOpen && (
                  <div className="px-5 pb-4 border-t border-luxury-border bg-luxury-elevated">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-4">
                      {[
                        { label: "Invested",     value: fmt(inv.amount_invested) },
                        { label: "Current Value",value: fmt(inv.current_value) },
                        { label: "Gain / Loss",  value: `${isGain?"+":""}${fmt(inv.gain_loss)}`, color: isGain?"text-luxury-cta":"text-red-600 dark:text-red-400" },
                        { label: "Return",       value: fmtPct(inv.gain_loss_pct), color: isGain?"text-luxury-cta":"text-red-600 dark:text-red-400" },
                        ...(inv.units > 0 ? [{ label: "Units", value: inv.units.toLocaleString() }] : []),
                        ...(inv.unit_price > 0 ? [{ label: "Unit Price", value: fmt(inv.unit_price) }] : []),
                        ...(inv.interest_rate > 0 ? [{ label: "Rate / Return", value: `${inv.interest_rate}% p.a.` }] : []),
                      ].map((f,i) => (
                        <div key={i}>
                          <p className="text-xs font-semibold text-luxury-muted uppercase tracking-wide mb-1">{f.label}</p>
                          <p className={`text-sm font-semibold ${f.color || "text-luxury-text"}`}>{f.value}</p>
                        </div>
                      ))}
                    </div>
                    {inv.notes && <p className="text-xs text-luxury-secondary mb-3">{inv.notes}</p>}
                    <button onClick={() => handleDelete(inv.id)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-surface border border-red-200 dark:border-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── SA Investment Tips ───────────────────────────────────── */}
      {investments.length > 0 && (
        <div className="mt-6 rounded-2xl bg-luxury-surface border border-luxury-border p-5">
          <h3 className="font-semibold text-luxury-text text-sm mb-3">💡 South Africa Investment Tips</h3>
          <div className="space-y-2">
            {[
              "Tax-Free Savings Account (TFSA) — max R36,000/year, R500,000 lifetime — zero tax on returns",
              "Retirement Annuity (RA) — contributions are tax-deductible up to 27.5% of income",
              "JSE ETFs (like Satrix, CoreShares) offer low-cost exposure to SA and global markets",
              "EasyEquities allows fractional shares — start investing from as little as R10",
              "Diversify across asset classes — shares, property, bonds, and cash for balanced risk",
            ].map((tip,i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-luxury-elevated flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-luxury-gold">{i+1}</span>
                </span>
                <p className="text-xs text-luxury-secondary">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAdd && <AddInvestmentModal onClose={() => setShowAdd(false)} onAdd={handleAdd} token={token} />}
    </div>
  );
}
